import { db } from "../../../../firebase/firebase";
import { doc, setDoc } from "firebase/firestore";

import {
  createEmptySheet,
  finalizeSheetTotals,
  createDefaultExpensesTemplate,
} from "../helpers";

import { formatDateLocal } from "./assignmentPeriods";

/**
 * Creates AND saves a manual driver sheet to Firestore.
 *
 * Important:
 * - carNumber must NOT start with MANUAL-, otherwise UI shows it as a manual/last sheet.
 * - isManualDriverSheet is enough to identify it internally.
 * - Firestore doc ID can still include MANUAL so it does not conflict with auto sheets.
 */
export async function createManualDriverSheet({
  month,
  driverName,
  company,
  planType,
}) {
  if (!driverName || !driverName.trim()) {
    throw new Error("Driver name / car number is required");
  }

  if (!company || !company.trim()) {
    throw new Error("Company is required for manual sheet");
  }

  if (!planType || !planType.trim()) {
    throw new Error("Plan type is required for manual sheet");
  }

  // Normalize
  driverName = driverName.trim();
  company = company.trim();
  planType = planType.trim();

  // Build monthStart / monthEnd
  const [year, m] = month.split("-");
  const monthStart = new Date(Number(year), Number(m) - 1, 1);
  const monthEnd = new Date(Number(year), Number(m), 0);

  // Visible car number/name in UI.
  // Do NOT prefix MANUAL here, otherwise it appears differently and sorts at the bottom.
  const carNumber = driverName;

  // Internal safe ID part
  const normalizedName = driverName
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[\/#?\[\]]+/g, "-")
    .toLowerCase();

  // Create sheet base
  const sheet = createEmptySheet({
    carNumber,
    carModel: "",
    company,
    month,
    periodStart: monthStart,
    periodEnd: monthEnd,
    planType,
  });

  // Identify internally as manual sheet
  sheet.isManualDriverSheet = true;

  // Required by UI components + Summary + Settlement
  sheet.driverCompany = company;
  sheet.drivers = [driverName];
  sheet.driverName = driverName;
  sheet.driverId = `MANUAL-${normalizedName}`;

  // Week + totals fields
  sheet.week1 = 0;
  sheet.week2 = 0;
  sheet.week3 = 0;
  sheet.week4 = 0;
  sheet.week5 = 0;

  sheet.totalIncome = 0;
  sheet.totalExpenses = 0;
  sheet.balance = 0;
  sheet.totalBankIncome = 0;
  sheet.totalCashTrips = 0;

  // Initialize manual income rows
  sheet.incomeRows = [
    {
      id: "otherAdj",
      rowType: "data",
      company: "OTHER ADJUSTMENT",
      driverId: sheet.driverId,
      driverName,
      driver: driverName,
      week1: 0,
      week2: 0,
      week3: 0,
      week4: 0,
      week5: 0,
      total: 0,
    },
    {
      id: "paymentReceived",
      rowType: "data",
      company: "PAYMENT RECEIVED",
      driverId: sheet.driverId,
      driverName,
      driver: driverName,
      week1: 0,
      week2: 0,
      week3: 0,
      week4: 0,
      week5: 0,
      total: 0,
    },
    {
      id: "adjustmentLC",
      rowType: "data",
      company: "CASH RECEIVED / PAID TO DRIVER",
      driverId: sheet.driverId,
      driverName,
      driver: driverName,
      week1: 0,
      week2: 0,
      week3: 0,
      week4: 0,
      week5: 0,
      total: 0,
    },
  ];

  // Manual sheets do not have auto cash rows
  sheet.cashRows = [];

  // Load full default expense template same as auto sheets
  sheet.expensesRows = createDefaultExpensesTemplate();

  // Display label in UI
  sheet.periodLabel = `${formatDateLocal(monthStart)} → ${formatDateLocal(
    monthEnd
  )}`;

  // Compute initial totals and add bottom summary rows
  finalizeSheetTotals(sheet);

  // Re-apply planType after finalize, in case helper overwrites anything
  sheet.planType = planType;

  // Hidden/internal Firestore document ID.
  // MANUAL remains only in the document ID, not in visible carNumber.
  const docId = `MANUAL-${normalizedName}|${month}|${formatDateLocal(
    monthStart
  )}|${formatDateLocal(monthEnd)}`;

  sheet.id = docId;

  await setDoc(doc(db, "carSheets", month, "sheets", docId), sheet, {
    merge: true,
  });

  return sheet;
}